import MonthYearRangePicker from "@components/inputs/date/MonthYearRangePicker";
import { TSimpleFormatter, TSimpleParser } from "@components/inputs/input/WithFormatter";
import { NumericInput } from "@components/inputs/numericInput/NumericInput";
import { GroupDividerSize, ISelectGroup } from "@components/inputs/select/Select.types";
import { TFilterDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { isDefined, isObjectEmpty } from "@utils/general";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { BasicInputSizes, IconSize, TextAlign, ValidationErrorType, ValueType } from "../../enums";
import { IValidationError, ValidationMessage } from "../../model/Validator.types";
import TestIds from "../../testIds";
import memoizeOneWithParamsCheck from "../../utils/memoizeOne";
import { IconButton } from "../button";
import { CaretIcon, CloseIcon } from "../icon";
import { DatePicker, DateRangePicker, MonthYearPicker } from "../inputs/date";
import { IDayInterval, isValidDateInterval } from "../inputs/date/utils";
import Input, { IInputOnChangeEvent, ISharedInputProps } from "../inputs/input";
import { IValidationErrorInterval, NumericRange } from "../inputs/numericRange";
import { BasicSelect } from "../inputs/select/BasicSelect";
import { StyledConditionalFilterRow, ValueInput } from "./ConditionalFilterDialog.styles";
import {
    Condition,
    ConditionType,
    defaultValue,
    getComplexFilterValue,
    IComplexFilter,
    isInterval,
    IValueInterval,
    PredefinedFilter,
    TFilterValue,
    ValueTypeAndConditionToPredefinedFilter,
    ValueTypeToCondition
} from "./ConditionalFilterDialog.utils";

export type TFilterValueError = IValidationError | IValidationErrorInterval;
export type TGetCustomValueInput = (row: IComplexFilter, onChange: (value: IComplexFilter) => void) => React.ReactElement;

export enum ConditionalFieldType {
    Default,
    MonthYear,
}

export interface IProps extends WithTranslation, Pick<TFilterDef, "parser" | "formatter"> {
    label: string;
    row: IComplexFilter;
    error: TFilterValueError;
    type: ValueType;
    subType?: ConditionalFieldType;
    canRemove?: boolean;
    isLabelVisible?: boolean;
    isEnum?: boolean;
    getCustomValueInput?: TGetCustomValueInput;
    validator?: (value: TFilterValue) => void;

    onChange: (val: IComplexFilter) => void;
    onRemove: (id: string) => void;
    onValueError: (rowId: string, error: TFilterValueError) => void;
}

class ConditionalFilterRow extends React.PureComponent<IProps> {
    prepareItems = memoizeOneWithParamsCheck((type: ValueType, condition: Condition) => {
        const _createItem = (key: any) => {
            return {
                id: key,
                label: this.props.t(`Components:ValueHelper.${key}`)
            };
        };

        const conditions = this.props.isEnum ? [_createItem(Condition.Equals)] : ValueTypeToCondition[type].map(_createItem);
        const filters = ValueTypeAndConditionToPredefinedFilter[type]?.[condition]?.map(_createItem);
        const types = [ConditionType.Included, ConditionType.Excluded].map(_createItem);

        return { conditions, filters, types };
    }, () => [this.props.tReady]);

    get isValueVisible() {
        return !!this.props.row.filter;
    }

    /**
     * Filter values could be 2 value interval or Array, otherwise it will be simple value...
     * @param v1
     * @param v2
     */
    valueTypeDiffers(v1: TFilterValue, v2: TFilterValue) {
        return isInterval(v1) !== isInterval(v2) || Array.isArray(v1) !== Array.isArray(v2);
    }


    changeRow(newRow: IComplexFilter) {
        const prevRow = this.props.row,
            prevRealValue = getComplexFilterValue(prevRow),
            defVal = defaultValue(newRow, this.props.type, this.props.isEnum);

        if (newRow.filter !== PredefinedFilter.Value) {
            // new row value has predefined filter -> there will be no value at all
            delete newRow.value;
        } else if (prevRow.filter !== PredefinedFilter.Value && !this.valueTypeDiffers(prevRealValue, defVal)) {
            // previous row value type was predefined and is of the same type
            //  -> keep that value to be edited by user
            newRow.value = prevRealValue;
        } else if (this.valueTypeDiffers(newRow.value, defVal)) {
            // new value type differs from the defaultOne -> reinitiate
            newRow.value = defVal;
        }
        this.props.onChange(newRow);
    }

    validateRow(row: IComplexFilter) {
        let error: TFilterValueError;
        // We need to validate Number range separately as two fields are used
        if (isInterval(row.value)) {
            let errorInterval = {} as IValidationErrorInterval;
            // Validate both interval values (DatePicker), fall on first error
            const value = row.value as IValueInterval;
            (["from", "to"] as (keyof IValueInterval)[]).forEach((k) => {
                try {
                    this.props.validator?.(value[k] as TFilterValue);
                } catch (e) {
                    errorInterval[k] = e;
                }
            });
            if (isObjectEmpty(errorInterval) && isDefined(value.from) && isDefined(value.to)) {
                const isValidInterval = this.props.type === ValueType.Date
                    ? isValidDateInterval(value as IDayInterval)
                    : value.from < value.to;
                if (!isValidInterval) {
                    const invRangeError = {
                        errorType: ValidationErrorType.Field,
                        message: ValidationMessage.NotARange
                    };
                    errorInterval = {
                        from: invRangeError, to: invRangeError
                    };
                }
            }
            error = errorInterval;
        } else if (Array.isArray(row.value)) {
            // do not validate array values, they are always valid
            // condition is used for Enum rows, which uses selects with predefined values
        } else {
            try {
                // validate single value
                this.props.validator?.(row.value);
            } catch (e) {
                error = e;
            }
        }
        // empty error object means no error actually (we might delete the last one from interval)
        if (isObjectEmpty(error)) {
            error = null;
        }
        this.props.onValueError(row.id, error);
    }

    handleTypeChange = (args: any) => {
        if (args.triggerAdditionalTasks) {
            const newRow = {
                ...this.props.row,
                type: args.value
            };
            this.changeRow(newRow);
            this.validateRow(newRow);
        }
    };

    handleConditionChange = (args: any) => {
        if (args.triggerAdditionalTasks) {
            const newRow = {
                ...this.props.row,
                filter: PredefinedFilter.Value,
                condition: args.value
            };
            this.changeRow(newRow);
            this.validateRow(newRow);
        }
    };

    handleFilterChange = (args: any) => {
        if (args.triggerAdditionalTasks) {
            const newRow = {
                ...this.props.row,
                filter: args.value
            };
            this.changeRow(newRow);
            if (args.value === PredefinedFilter.Value) {
                this.validateRow(newRow);
            } else {
                // it's filter with predefined value -> it's always valid (value is not defined)
                //  -> clear error
                this.props.onValueError(newRow.id, null);
            }
        }
    };

    handleValueChange = (value: TFilterValue) => {
        const newRow = {
            ...this.props.row,
            value
        };

        this.changeRow(newRow);
        this.validateRow(newRow);
    };

    handleInputChange = (event: IInputOnChangeEvent) => {
        this.handleValueChange(event.value);
    };

    handleBlur = () => {
        if (this.props.error) {
            // rerender components to show error on focus out
            this.forceUpdate();
        }
    };

    handleRemove = () => {
        this.props.onRemove(this.props.row.id);
    };

    renderValueInput(row: IComplexFilter) {
        const value = getComplexFilterValue(row);

        const _singleInputByType = (value: TFilterValue, type: ValueType, props: ISharedInputProps) => {
            if (this.props.getCustomValueInput) {
                return this.props.getCustomValueInput(row, this.props.onChange);
            }

            switch (type) {
                case ValueType.Date:
                    if (this.props.subType === ConditionalFieldType.MonthYear) {
                        return (
                            <MonthYearPicker {...props}
                                             value={value as Date}
                                             error={this.props.error as IValidationError}
                                             onChange={this.handleInputChange}
                            />
                        );
                    }
                    return (
                        <DatePicker {...props}
                                    value={value as Date}
                                    error={this.props.error as IValidationError}
                                    onChange={this.handleInputChange}
                        />
                    );
                case ValueType.Number:
                    return (
                        <NumericInput {...props}
                                      value={value as number}
                                      error={this.props.error as IValidationError}
                                      onChange={this.handleInputChange}
                        />
                    );
                default:
                    return (
                        <Input {...props}
                               value={value as string}
                               error={this.props.error as IValidationError}
                               onChange={this.handleInputChange}
                        />
                    );
            }
        };

        const _doubleInputByType = (value: TFilterValue, type: ValueType, props: ISharedInputProps) => {
            const error = this.props.error as IValidationErrorInterval;
            switch (type) {
                case ValueType.Date:
                    if (this.props.subType === ConditionalFieldType.MonthYear) {
                        return (
                            <MonthYearRangePicker {...props}
                                                 value={value as IValueInterval<Date>}
                                                 error={(error?.from ?? error?.to) as IValidationError}
                                                 onChange={this.handleInputChange}
                            />
                        );
                    }
                    return (
                        <DateRangePicker {...props}
                                         value={value as IDayInterval}
                                         error={(error?.from ?? error?.to) as IValidationError}
                                         onChange={this.handleInputChange}
                        />
                    );
                case ValueType.Number:
                    return (
                        <NumericRange {...props}
                                      value={value as IValueInterval<number>}
                                      error={error}
                                      onChange={this.handleValueChange}
                        />
                    );
                default:
                    // We don't have double input for other types, fallback to single one
                    return _singleInputByType(value, type, props);
            }
        };

        const isDateFilter = this.props.type === ValueType.Date;

        const sharedProps = {
            width: isDateFilter ? BasicInputSizes.L : BasicInputSizes.XL,
            textAlign: TextAlign.Left,
            onBlur: this.handleBlur,
            isDisabled: row.filter !== PredefinedFilter.Value,
            isSharpLeft: true,
            parser: this.props.parser as unknown as TSimpleParser,
            formatter: this.props.formatter as TSimpleFormatter
        };

        const getValueControls = isInterval(value) ? _doubleInputByType : _singleInputByType;

        return (
            <ValueInput>{getValueControls(value, this.props.type, sharedProps)}</ValueInput>
        );
    }

    render() {
        const selectProps = {
            width: BasicInputSizes.M,
            inputIcon: <CaretIcon width={IconSize.M}/>,
            closeOnSelection: true,
            openOnClick: true,
            inputIsReadOnly: true
        };

        const defaultValueGroup = {
                id: "Default"
            },
            itemsGroup: ISelectGroup = { id: undefined, dividerSize: GroupDividerSize.Medium },
            defaultValue = {
                id: PredefinedFilter.Value,
                label: this.props.t(`Components:ValueHelper.${this.props.type === ValueType.Date ? "DateValue" : "Value"}`),
                groupId: defaultValueGroup.id
            };

        const items = this.prepareItems(this.props.type, this.props.row.condition);

        return (
            <StyledConditionalFilterRow data-testid={TestIds.ValueHelperRow}>
                <BasicSelect {...selectProps}
                             items={items.types}
                             isSharpRight
                             value={this.props.row.type}
                             onChange={this.handleTypeChange}
                />
                {items.conditions?.length &&
                    <BasicSelect {...selectProps}
                                 width={BasicInputSizes.L}
                                 isDisabled={items.conditions?.length === 1}
                                 items={items.conditions}
                                 isSharpLeft isSharpRight
                                 value={this.props.row.condition}
                                 onChange={this.handleConditionChange}
                    />}

                {items.filters?.length &&
                    <BasicSelect {...selectProps}
                                 groups={[defaultValueGroup, itemsGroup]}
                                 items={[defaultValue, ...items.filters]}
                                 isSharpLeft isSharpRight
                                 value={this.props.row.filter}
                                 onChange={this.handleFilterChange}
                    />}

                {this.isValueVisible && this.renderValueInput(this.props.row)}

                <IconButton title={this.props.t("Components:ValueHelper.Remove")}
                            onClick={this.handleRemove}
                            isDisabled={!this.props.canRemove}
                            isDecorative>
                    <CloseIcon/>
                </IconButton>
            </StyledConditionalFilterRow>
        );
    }
}

export default withTranslation(["Components"])(ConditionalFilterRow);